/**
 * useNarrativeFeed.js — Phase 1.3: Unified Narrative Feed Composable
 *
 * Subscribes to socket events across all agents and maintains a reactive
 * feed buffer (max 500 events in memory). Older events paginated from API.
 *
 * Usage:
 *   const { events, loading, hasMore, loadMore, stats } = useNarrativeFeed({
 *     maxBuffer: 500,
 *     agents: ['centinela', 'jefe', 'supervisor'],
 *     severities: ['critical', 'high', 'medium', 'low', 'info'],
 *   })
 */

import { useSocket, Events, connectSocket } from '@/services/SocketService'
import AgentEventService from '@/services/AgentEventService'

const MAX_BUFFER_DEFAULT = 500

/**
 * Map raw socket/API event to normalized NarrativeEvent shape
 */
function normalizeEvent(raw) {
  return {
    id: raw.id || raw._id || `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    agent: raw.agent || resolveAgent(raw),
    severity: raw.severity || resolveSeverity(raw),
    title: raw.title || raw.message || raw.description || 'Event',
    narrative: raw.narrative || raw.detail || raw.text || '',
    source: raw.source || raw.device_name || raw.asset_name || '',
    sourceId: raw.device_id || raw.asset_id || raw.source_id || '',
    timestamp: raw.timestamp || raw.created_at || new Date().toISOString(),
    type: raw.type || raw.event_type || 'info',
    data: raw.data || raw.payload || null,
    read: false,
    _raw: raw,
  }
}

function resolveAgent(raw) {
  const type = (raw.type || raw.event_type || '').toLowerCase()
  if (type.includes('security') || type.includes('intrusion') || type.includes('camera')) return 'centinela'
  if (type.includes('fleet') || type.includes('vehicle') || type.includes('mission') || type.includes('route')) return 'jefe'
  if (type.includes('safety') || type.includes('process') || type.includes('line') || type.includes('production')) return 'supervisor'
  // Default based on alert/telemetry
  if (raw.event_type === 'alert' || raw.type === 'alert') return 'centinela'
  return 'jefe'
}

function resolveSeverity(raw) {
  const sev = (raw.severity || raw.level || '').toLowerCase()
  if (['critical', 'high', 'medium', 'low', 'info', 'ok'].includes(sev)) return sev
  // Infer from event type
  if (raw.type === 'alert' || raw.event_type === 'alert') return 'high'
  if (raw.type === 'warning') return 'medium'
  return 'info'
}

export function useNarrativeFeed(options = {}) {
  const {
    maxBuffer = MAX_BUFFER_DEFAULT,
    agents = null,       // null = all agents, or ['centinela', 'jefe', 'supervisor']
    severities = null,   // null = all, or ['critical', 'high']
    autoConnect = true,
  } = options

  /* ─── Reactive state ────────────────────────────────────────── */
  const events = ref([])
  const loading = ref(false)
  const loadingMore = ref(false)
  const hasMore = ref(true)
  const page = ref(1)
  const PAGE_SIZE = 20

  // Filter state (can be changed reactively by the UI)
  const activeAgentFilter = ref(agents ? [...agents] : ['centinela', 'jefe', 'supervisor'])
  const activeSeverityFilter = ref(severities ? [...severities] : ['critical', 'high', 'medium', 'low', 'info', 'ok'])

  /* ─── Computed: filtered events ──────────────────────────────── */
  const filteredEvents = computed(() => {
    return events.value.filter(evt => {
      if (!activeAgentFilter.value.includes(evt.agent)) return false
      if (!activeSeverityFilter.value.includes(evt.severity)) return false
      return true
    })
  })

  /* ─── Stats ─────────────────────────────────────────────────── */
  const stats = computed(() => {
    const s = { total: events.value.length, unread: 0, byAgent: {}, bySeverity: {} }
    events.value.forEach(evt => {
      if (!evt.read) s.unread++
      s.byAgent[evt.agent] = (s.byAgent[evt.agent] || 0) + 1
      s.bySeverity[evt.severity] = (s.bySeverity[evt.severity] || 0) + 1
    })
    return s
  })

  /* ─── Add event to buffer (with dedup + cap) ────────────────── */
  function addEvent(rawEvent) {
    const normalized = normalizeEvent(rawEvent)

    // Dedup by id
    if (events.value.some(e => e.id === normalized.id)) return

    // Prepend (newest first)
    events.value.unshift(normalized)

    // Cap buffer
    if (events.value.length > maxBuffer) {
      events.value.splice(maxBuffer)
      hasMore.value = true
    }
  }

  /* ─── Socket subscription ────────────────────────────────────── */
  const { on } = useSocket()

  function subscribe() {
    if (autoConnect) connectSocket()

    // Listen to narrative events (primary feed channel)
    on(Events.NARRATIVE_EVENT, (payload) => {
      addEvent(payload)
    })

    // Also listen to device events (they generate feed entries)
    on(Events.DEVICE_EVENT, (payload) => {
      addEvent({
        ...payload,
        type: 'alert',
        agent: resolveAgent(payload),
        severity: payload.severity || 'high',
        title: payload.title || `Alert: ${payload.message || 'Asset alert triggered'}`,
        narrative: payload.detail || payload.message || '',
        source: payload.device_name || payload.asset_name || '',
      })
    })

    // Mission updates generate feed entries
    on(Events.MISSION_UPDATE, (payload) => {
      addEvent({
        ...payload,
        type: 'mission',
        agent: 'jefe',
        severity: 'info',
        title: payload.title || `Mission Update`,
        narrative: payload.detail || payload.message || '',
      })
    })

    // Agent status changes
    on(Events.AGENT_STATUS, (payload) => {
      addEvent({
        ...payload,
        type: 'agent_status',
        severity: payload.status === 'error' ? 'high' : 'info',
        title: `${payload.agent_name || 'Agent'} is now ${payload.status}`,
      })
    })
  }

  /* ─── API: load historical events ───────────────────────────── */
  async function loadInitial() {
    loading.value = true
    try {
      const data = await AgentEventService.getEvents({ page: 1, limit: PAGE_SIZE, sort: '-created_at' })
      const items = data?.items || data?.data || data || []
      items.forEach(item => addEvent(item))
      hasMore.value = items.length >= PAGE_SIZE
      page.value = 1
    } catch (e) {
      console.warn('[NarrativeFeed] Could not load historical events:', e.message)
      // Not blocking — feed will populate from socket events
    } finally {
      loading.value = false
    }
  }

  async function loadMore() {
    if (loadingMore.value || !hasMore.value) return
    loadingMore.value = true
    try {
      page.value++
      const data = await AgentEventService.getEvents({ page: page.value, limit: PAGE_SIZE, sort: '-created_at' })
      const items = data?.items || data?.data || data || []
      items.forEach(item => addEvent(item))
      hasMore.value = items.length >= PAGE_SIZE
    } catch (e) {
      console.warn('[NarrativeFeed] Error loading more events:', e.message)
      page.value--
    } finally {
      loadingMore.value = false
    }
  }

  /* ─── Actions ───────────────────────────────────────────────── */
  function markRead(eventId) {
    const evt = events.value.find(e => e.id === eventId)
    if (evt) evt.read = true
  }

  function markAllRead() {
    events.value.forEach(e => { e.read = true })
  }

  function clearEvents() {
    events.value = []
    page.value = 1
    hasMore.value = true
  }

  /* ─── Initialize ────────────────────────────────────────────── */
  subscribe()
  loadInitial()

  return {
    // Data
    events,
    filteredEvents,
    stats,

    // State
    loading,
    loadingMore,
    hasMore,

    // Filters
    activeAgentFilter,
    activeSeverityFilter,

    // Actions
    loadMore,
    markRead,
    markAllRead,
    clearEvents,
    addEvent,
  }
}
